import { useMemo } from 'react';
import useSensorDetailsInLoc from '../LocationConfig/Tagging/useSensorDetailsInLoc';
import { useAppSelector } from '../../state/store';
import { getActiveMarker, getSensorsByVarName } from '../../state/selectors';
import { SensorLatest } from '../../services/api';
import { getAllSpaces, SpaceParams } from './mapHelpers';

// processAllSpaces shows all tag spaces, else will show spaces based on active var sensors
export default function useLocAllSpaces(processAllSpaces?: boolean): SpaceParams[] {
  const sensorsDetailsInLoc = useSensorDetailsInLoc();
  const activeMarker = useAppSelector(getActiveMarker);
  const sensorsByVarName = useAppSelector(getSensorsByVarName);

  const activeVarNameSensors = useMemo(() => {
    const sensorIds = sensorsByVarName.get(activeMarker);
    return sensorsDetailsInLoc.filter((sensor) => sensorIds?.includes(sensor.id));
  }, [activeMarker, sensorsByVarName, sensorsDetailsInLoc]);

  const allActiveSpaces = useMemo(
    () =>
      Array.from(
        new Set(activeVarNameSensors.flatMap((sensor: SensorLatest) => sensor?.tags ?? []))
      ),
    [activeVarNameSensors]
  );

  // all sensors in the location, grouped by tag and alphabetically sorted -
  // single pass, shared with the plain getAllSpaces helper
  const allSpaces = useMemo(() => getAllSpaces(sensorsDetailsInLoc) ?? [], [sensorsDetailsInLoc]);

  const activeSpaces = useMemo(() => {
    if (processAllSpaces) return allSpaces;

    // only show spaces from active marker sensors - filter the already-grouped
    // list down to the active tags instead of re-scanning sensors per tag
    const activeTagSet = new Set(allActiveSpaces);
    return allSpaces.filter((space) => activeTagSet.has(space.title));
  }, [allActiveSpaces, allSpaces, processAllSpaces]);

  return activeSpaces;
}
